import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Upload, X, FileIcon, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { uploadToBucket } from "@/lib/uploadFile";

interface FileDropZoneProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  disabled?: boolean;
  /** Max files allowed in this batch. Default 5. */
  maxFiles?: number;
  /** Max bytes per file. Default 10 MB. */
  maxBytes?: number;
  /** Accepted MIME-prefix list. Default images/PDF/text/zip/log. */
  accept?: string[];
}

const DEFAULT_ACCEPT = [
  "image/",
  "application/pdf",
  "text/",
  "application/zip",
  "application/x-zip-compressed",
];

export default function FileDropZone({
  files,
  onFilesChange,
  disabled,
  maxFiles = 5,
  maxBytes = 10 * 1024 * 1024,
  accept = DEFAULT_ACCEPT,
}: FileDropZoneProps) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragging(true);
    else if (e.type === "dragleave") setDragging(false);
  }, []);

  const isAccepted = (f: File) => !f.type || accept.some((a) => f.type.startsWith(a));

  const addFiles = useCallback((newFiles: FileList | null) => {
    if (!newFiles) return;
    const arr = Array.from(newFiles);
    const tooBig = arr.filter((f) => f.size > maxBytes);
    const wrongType = arr.filter((f) => !isAccepted(f));
    let valid = arr.filter((f) => f.size <= maxBytes && isAccepted(f));
    if (tooBig.length) toast.error(`Some files exceed ${Math.round(maxBytes / 1024 / 1024)}MB limit`);
    if (wrongType.length) toast.error("Unsupported file type");
    const remaining = Math.max(0, maxFiles - files.length);
    if (valid.length > remaining) {
      toast.error(`You can attach up to ${maxFiles} files`);
      valid = valid.slice(0, remaining);
    }
    if (valid.length) onFilesChange([...files, ...valid]);
  }, [files, onFilesChange, maxBytes, maxFiles, accept]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const removeFile = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const isImage = (file: File) => file.type.startsWith("image/");

  return (
    <div className="space-y-2">
      <div
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          dragging ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/50"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
      >
        <Upload className="h-5 w-5 mx-auto mb-1 text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          Drag & drop files here, or <span className="text-primary font-medium">browse</span>
        </p>
        <p className="text-[10px] text-muted-foreground mt-0.5">
          Up to {maxFiles} files · max {Math.round(maxBytes / 1024 / 1024)}MB each · images, PDF, text, zip
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          disabled={disabled}
        />
      </div>

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file, i) => (
            <div key={i} className="relative group flex items-center gap-1.5 bg-muted rounded-md px-2 py-1 text-xs">
              {isImage(file) ? <ImageIcon className="h-3.5 w-3.5 text-primary" /> : <FileIcon className="h-3.5 w-3.5 text-muted-foreground" />}
              <span className="max-w-[120px] truncate">{file.name}</span>
              <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(i); }} className="ml-0.5 text-muted-foreground hover:text-destructive">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Uploads to the private `support-attachments` bucket under `<uid>/<ticketId>/<uuid>.<ext>`
 * and returns the storage paths (not public URLs).
 */
export async function uploadFiles(files: File[], ticketId: string): Promise<string[]> {
  const paths: string[] = [];
  const { data: auth } = await supabase.auth.getUser();
  const uid = auth.user?.id;
  if (!uid) {
    toast.error("You must be signed in to attach files");
    return paths;
  }
  for (const file of files) {
    try {
      const { path } = await uploadToBucket({
        bucket: "support-attachments",
        file,
        userId: uid,
        prefix: ticketId,
      });
      paths.push(path);
    } catch (err: any) {
      toast.error(`Failed to upload ${file.name}: ${err.message}`);
      continue;
    }
  }
  return paths;
}

/** Resolves a stored value (legacy public URL or new storage path) to a viewable URL. */
async function resolveUrl(value: string): Promise<string | null> {
  if (/^https?:\/\//i.test(value)) return value;
  const { data } = await supabase.storage
    .from("support-attachments")
    .createSignedUrl(value, 60 * 10);
  return data?.signedUrl ?? null;
}

export function AttachmentDisplay({ attachments }: { attachments: string[] }) {
  const [urls, setUrls] = useState<(string | null)[]>([]);

  useEffect(() => {
    let active = true;
    if (!attachments || attachments.length === 0) {
      setUrls([]);
      return;
    }
    Promise.all(attachments.map(resolveUrl)).then((res) => {
      if (active) setUrls(res);
    });
    return () => {
      active = false;
    };
  }, [attachments]);

  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {attachments.map((value, i) => {
        const url = urls[i];
        const isImg = /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(value);
        if (!url) {
          return (
            <span key={i} className="flex items-center gap-1 text-xs text-muted-foreground bg-muted rounded px-2 py-1">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Attachment {i + 1}
            </span>
          );
        }
        return isImg ? (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer">
            <img src={url} alt="Support ticket attachment" className="h-16 w-16 object-cover rounded border" />
          </a>
        ) : (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline bg-muted rounded px-2 py-1">
            <FileIcon className="h-3.5 w-3.5" />
            Attachment {i + 1}
          </a>
        );
      })}
    </div>
  );
}

